import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { map } from "rxjs/operators";
import { AppSettings } from "../../../config/config";
import { apiConstants } from "../../constant/apiConstant";

export type LeaveDisplayStatus = 'Pending' | 'Approved' | 'Rejected' | 'Cancelled';

export interface LeaveAttachment {
  [key: string]: any;
}

export interface LeaveRequestItem {
  leaveRequestId: string;
  studentName: string;
  studentImage: string | null;
  leaveTypeName: string;
  displayStatus: LeaveDisplayStatus;
  startDate: string;
  endDate: string;
  startTime: string | null;
  endTime: string | null;
  reason: string;
  attachments: LeaveAttachment[];
  actionedByName: string | null;
  actionedByImage: string | null;
  remarks: string | null;
}

export interface LeaveClassGroup {
  classId: string;
  className: string;
  leaveRequests: LeaveRequestItem[];
}

export interface LeaveStatusCounts {
  pending: number;
  approved: number;
  rejected: number;
  cancelled: number;
}

export interface GetLeaveManagementListParams {
  status: 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  startDate: string;
  endDate: string;
  studentName: string;
  classIds: string;
  classCategoryIds: string;
  classStandardIds: string;
  page: number;
  limit: number;
}

export interface GetLeaveManagementListResponse {
  status: boolean;
  body?: {
    classes: LeaveClassGroup[];
    maxPageNumber: number;
    statusCounts: LeaveStatusCounts;
  };
}

export interface ActionLeaveRequestBody {
  leaveRequestId: string;
  action: 'APPROVE' | 'REJECT';
  remarks: string | null;
}

export interface ActionLeaveRequestResponse {
  status: boolean;
  message?: string;
}

export interface LeaveFilterOption {
  label: string;
  value: string;
}

// classStandardList groups standards under categories, e.g.
// [{ classCategoryId, classStandardList: [{ label, value }, ...] }, ...] —
// flattenGroupedList() below pulls every leaf option out into one flat
// array for the dropdown, same idea repeated (twice) for classSectionList
// which nests sections two levels deep (groups -> standards -> sections).
interface LeaveGroupedOption {
  [key: string]: any;
}

interface LeaveClassCategoryResponse {
  status: boolean;
  body?: { classCategoryListData: LeaveFilterOption[] };
}

interface LeaveClassStandardResponse {
  status: boolean;
  body?: { classStandardList: LeaveGroupedOption[] };
}

interface LeaveClassSectionResponse {
  status: boolean;
  body?: { classSectionList: LeaveGroupedOption[] };
}

@Injectable({ providedIn: 'root' })
export class StaffLeaveManagementService {
  private url = AppSettings.API_ENDPOINT_STAFF;

  constructor(private http: HttpClient) {}

  getLeaveManagementList(params: GetLeaveManagementListParams) {
    const httpParams = new HttpParams()
      .set('status', params.status)
      .set('startDate', params.startDate)
      .set('endDate', params.endDate)
      .set('studentName', params.studentName)
      .set('classIds', params.classIds)
      .set('classCategoryIds', params.classCategoryIds)
      .set('classStandardIds', params.classStandardIds)
      .set('page', String(params.page))
      .set('limit', String(params.limit));
    return this.http
      .get<GetLeaveManagementListResponse>(this.url + apiConstants.getStudentLeaveManagementList, { params: httpParams })
      .pipe(map((res) => res));
  }

  actionLeaveRequest(body: ActionLeaveRequestBody) {
    return this.http
      .put<ActionLeaveRequestResponse>(this.url + apiConstants.actionStudentLeaveRequest, body)
      .pipe(map((res) => res));
  }

  getClassCategoryOptions() {
    return this.http
      .get<LeaveClassCategoryResponse>(this.url + apiConstants.getLeaveManagementClassCategory)
      .pipe(map((res) => res));
  }

  getClassStandardOptions(classCategoryIds: string) {
    const params = new HttpParams().set('classCategoryIds', classCategoryIds);
    return this.http
      .get<LeaveClassStandardResponse>(this.url + apiConstants.getLeaveManagementClassStandard, { params })
      .pipe(map((res) => res));
  }

  getClassSectionOptions(classCategoryIds: string, classStandardIds: string) {
    const params = new HttpParams()
      .set('classCategoryIds', classCategoryIds)
      .set('classStandardIds', classStandardIds);
    return this.http
      .get<LeaveClassSectionResponse>(this.url + apiConstants.getLeaveManagementClassSection, { params })
      .pipe(map((res) => res));
  }

  // Both classStandardList and classSectionList arrive grouped (category ->
  // standards, and category -> standards -> sections respectively) rather
  // than as a flat option list like Check-In's equivalent endpoints — this
  // walks one level of grouping and returns the leaf { label, value }
  // options, discarding the grouping key. Call it twice for sections (once
  // per nesting level) per spec.
  flattenGroupedList(groups: LeaveGroupedOption[], childKey: string): LeaveFilterOption[] {
    const options: LeaveFilterOption[] = [];
    for (const group of groups || []) {
      const children = group?.[childKey];
      if (Array.isArray(children)) {
        for (const child of children) {
          if (child && 'label' in child && 'value' in child) {
            options.push({ label: child.label, value: child.value });
          } else {
            options.push(child);
          }
        }
      }
    }
    return options;
  }
}
